import { ProgramNames, TriageSearch } from '../../models'
import { CENTER_OF_EXCELLENCE } from '../centerOfExcellence/types'
import { SELF_CARE_MEDIA_EVENTS } from '../common/constants/mixpanelConstants'
import { DirectPathBookingApplinkParams } from '../searchForCare/constants'
import { SELF_CARE_MEDIA_TYPE, SelfCareMediaContent, SelfCareMediaImageContent } from '../selfCareMedia/types'

export enum WELLNESS_TOOLKIT_PROMOTION {
  WORK = 'work',
  PURPOSE = 'purpose',
  RELATIONSHIPS = 'relationships',
  PARENT = 'parent',
  FAMILY = 'family',
  STRESS = 'stress',
  WELLBEING = 'wellbeing',
  WORKING_WITH_OTHERS = 'working-with-others',
  MINDFULNESS = 'mindfulness',
  SELF_COMPASSION = 'self-compassion',
  LIFE_TRANSITIONS = 'life-transitions',
  CAREGIVER_STRESS = 'caregiver-stress',
  TRAUMA = 'trauma',
}

export type TOOLKIT_TYPE = WELLNESS_TOOLKIT_PROMOTION | CENTER_OF_EXCELLENCE

export enum ESSENTIALS_ARTICLE_SLUGS {
  FIELDS = 'fields',
  DEALING_WITH_DIFFICULT_PEOPLE = 'dealing-with-difficult-people',
  BALANCE_CIRCLE = 'balance-circle',
  WAVES = 'waves',
  AWARENESS_AND_SOFTNESS = 'awareness-and-softness',
  STOP_DROP_IN_AND_SOFTEN_WHAT_YOU_CAN = 'stop-drop-in-and-soften-what-you-can',
  STREAM_VISUALIZATION = 'stream-visualization',
  AWAKENING_YOUR_HEART = 'awakening-your-heart',
  SETTLING_THE_MIND_ON_THE_BREATH = 'settling-the-mind-on-the-breath',
  SMILING_ESCAPE = 'on-the-spot-meditation-smiling',
  WAKING_UP_TO_OUR_LIFE = 'waking-up-to-our-life',
}

export enum WELLNESS_TOOLKIT_TYPE {
  PARENT_COACHING = 'parentCoaching',
  INDIVIDUAL_COACHING = 'individualCoaching',
  FAMILY_HUB = 'familyHub',
}

export enum PROVIDER_SUPPLY_GROUP {
  PARENT_COACHING = 'parentCoaching',
  INDIVIDUAL_COACHING = 'individualCoaching',
  FAMILY_HUB = 'familyHub',
}

export type WellnessToolkitEssentialsMedia = {
  slug: ESSENTIALS_ARTICLE_SLUGS
  media: SELF_CARE_MEDIA_TYPE
  imageGIFOverride?: SelfCareMediaImageContent
}

export type WellnessToolkitNavigateToEssentialOnPress = {
  subPath: string
  title: string
  locale: string
  toggleTranscript?: string
  event?: SELF_CARE_MEDIA_EVENTS
  media?: SelfCareMediaContent
}

export type WellnessToolkitSelfCareMediaContent = {
  media: SelfCareMediaContent
  mediaType: SELF_CARE_MEDIA_TYPE
  navigateToEssentials: ({
    locale,
    title,
    subPath,
    toggleTranscript,
    event,
    media,
  }: WellnessToolkitNavigateToEssentialOnPress) => void
  userDisplayLanguage: string
  onLoad?: (media: SelfCareMediaContent) => void
  onAudioPlay?: (media: SelfCareMediaContent, duration: number) => void
}

export type SpeculativeSearchThresholds = {
  [key in PROVIDER_SUPPLY_GROUP]: { filters: { check: Array<SpeculativeFilter> } }
}

type Operators = 'lt' | 'gte'

type IntComparison = Partial<Record<Operators, number>>

export type SpeculativeFilterObject = { filters: { check: Array<SpeculativeFilter> } }

export type SpeculativeFilter = {
  numProviders: IntComparison
  calendarSlots?: IntComparison
}

/**
 * Each prop (excluding `configExtras`) is a condition that may or may not apply to a given toolkit:
 * - `true` = the condition applies and must be met for a given toolkit to be shown
 * - `false` = the condition does not apply to the given toolkit
 *
 * `configExtras` contains any additional configurations that is not a condition
 */
export interface WellnessToolkitAvailableConfig {
  shouldNotBeTeen: boolean
  shouldNotBeInternational: boolean
  shouldEssentialsBeEnabled: boolean
  shouldCoachingBeEnabled: boolean
  shouldTherapyBeEnabled: boolean
  shouldCoachingForParentsBeEnabled: boolean
  /** Checks shouldHideChildSearchCTA, hideDependentField */
  shouldDependentsBeSupported: boolean
  shouldNotBeSHOrSI: boolean
  /** Speculative search. Must pass `programSearchRequestData` in `configExtras` in order to run the search */
  shouldHaveProviderSupply: boolean
  shouldNotBeInActiveEpisode: boolean
  shouldNotHaveT3Recommendation: boolean
  shouldFeatureFlagBeEnabled: boolean
  shouldDisplayLanguageBeEnglish: boolean
  configExtras?: {
    allowedProgramsForActiveEpisodes?: ProgramNames[]
    programSearchRequestData?: DirectPathBookingApplinkParams
    triggerConditionsChecker?: ({
      triageSearches,
      userGender,
    }: {
      triageSearches: TriageSearch[]
      userGender?: string
    }) => boolean
    /** Override any conditions and always hide the toolkit */
    forceHideToolkit?: boolean
  }
}

/**
 * IMPORTANT: These enum values are stored in the database.
 * DO NOT modify existing values as it will break data for existing users.
 *
 * To safely evolve this enum:
 * 1. Add new values, don't modify or remove existing ones
 * 2. To "rename" a value, add a new one and deprecate the old one with a deprecated tag.
 * 3. To change display text, update the message mappings in constants.ts or the OnboardingWellnessGoalsWordCloud component
 * 4. Consider adding a database migration if removing/changing values
 *
 */
export enum WELLNESS_GOALS_WORD_CLOUD_OPTIONS {
  CAREGIVER_SUPPORT = 'CAREGIVER_SUPPORT',
  MINDFULNESS = 'MINDFULNESS',
  MANAGE_STRESS = 'MANAGE_STRESS',
  PARENTING_SUPPORT = 'PARENTING_SUPPORT',
  WORK_WITH_OTHERS = 'WORK_WITH_OTHERS',
  LIFE_TRANSITIONS = 'LIFE_TRANSITIONS',
  PRACTICE_SELF_COMPASSION = 'PRACTICE_SELF_COMPASSION',
  THRIVE_AT_WORK = 'THRIVE_AT_WORK',
  STRENGTHEN_RELATIONSHIPS = 'STRENGTHEN_RELATIONSHIPS',
  IMPROVE_YOUR_WELLBEING = 'IMPROVE_YOUR_WELLBEING',
  IDENTIFY_VALUES = 'IDENTIFY_VALUES',
}

export type PostWellnessGoalsPayload = { goals: WELLNESS_GOALS_WORD_CLOUD_OPTIONS[] }

export type WellnessGoalsResponse = { goals: WELLNESS_GOALS_WORD_CLOUD_OPTIONS[] }

export type ToolkitAvailabilityReason = {
  propertyName: string
  value?: boolean | number
}

interface ToolkitEvaluationInfo {
  toolkitLabel: string
  conditions: WellnessToolkitAvailableConfig
  configExtras?: WellnessToolkitAvailableConfig['configExtras']
}

export interface ToolkitShortCircuitEligibilityInputs extends ToolkitEvaluationInfo {
  isFeatureFlagEnabled?: boolean
  isEssentialsEnabled: boolean
  isCoachingEnabled: boolean
  isTherapyEnabled: boolean
  isCoachingForParentsEnabled: boolean
  hideChildSearchCTA?: boolean
  hideDependentField?: boolean
  isUserTeen: boolean
  isInternationalUser: boolean
  isUserDisplayLanguageEnglish: boolean
}

export interface ToolkitAvailabilityInputs extends ToolkitShortCircuitEligibilityInputs {
  isNotSHOrSI: boolean
  isNotInActiveEpisode: boolean
  isNotT3Recommendation: boolean
  shouldHaveProviderSupply: boolean
  hasAvailableProviderSupply: boolean
}

export type ToolkitAvailabilityResult = {
  hasPassedShortCircuitEligibilityChecks: boolean
  isToolkitAvailable: boolean
  reasons: ToolkitAvailabilityReason[]
}
export type ShortCircuitEligibilityResult = {
  hasPassedShortCircuitEligibilityChecks: boolean
  reasons: ToolkitAvailabilityReason[]
}
